<template>
  <v-container fluid class="white pb-10 pt-7 px-5">
    <div class="d-flex align-center gap-2">
      <back-btn />
      <div class="outlined-card pa-4">
        <icon-title text="Pedido carretes" icon="mdi-bookmark-check" />
      </div>
    </div>

    <blocked-by-backorders-alert
      v-if="userIsBlockedByBackorders"
      class="mt-5"
    />

    <div v-if="!orderCreated">
      <customer-order-information
        :customer-data="customerData"
        :customer-channel="customerData?.CUSTOMER?.channel || ''"
        :past-due-balance="walletPastDueBalanceFormated"
        :current-order-status="orderStatus"
        :current-order-total="cartTotals.total"
        :available-credit="availableCreditFormated"
        :loading="loadingCustomerData"
        :error="customerDataError || loadCustomerError"
      />

      <v-card
        v-if="isACreditCustomer"
        class="pa-5 my-5 card-shadow"
        rounded="0"
      >
        <tax-data-form
          v-model="orderForm"
          :payment-methods="paymentValues.paymentMethod"
          :ways-to-pay="paymentValues.wayToPay"
          :cfdi-uses="paymentValues.cfdiUse"
        />
      </v-card>

      <v-card class="pa-5 my-5 card-shadow" rounded="0">
        <order-information-form
          v-model="orderForm"
          :cfdi-relations="paymentValues.cfdiRelac"
          :address-list="shippingAddressList"
          show-cfdi-two
        />
      </v-card>

      <icon-title class="mt-md-10" text="Catálogo de carretes" />

      <v-row class="my-5">
        <v-col v-if="reelErrorMessage.length > 0" cols="12">
          <v-alert text color="warning">
            {{ reelErrorMessage }}
          </v-alert>
        </v-col>

        <v-col cols="12">
          <div class="d-sm-flex d-flex-wrap align-center justify-space-between">
            <v-btn
              class="card-shadow text-capitalize"
              dark
              color="#006cb1"
              :block="isMobile"
              @click="fullReelsTable = !fullReelsTable"
            >
              {{ fullHeaderReelsTableTextBtn }}
            </v-btn>
            <search-input
              v-model="searchReel"
              placeholder="Buscar carrete"
              :default-width="!isMobile"
              hide-details
              class="my-4"
            />
          </div>
          <reels-table
            :value="reelsSelected"
            :reels="reels"
            :loading="loadingReels"
            :search="searchReel"
            :full-table="fullReelsTable"
            :no-selectable="loadingCustomerData || userIsBlockedByBackorders"
            @itemSelected="modifyCart"
          />
        </v-col>
      </v-row>

      <!-- In some cases cart could get empty and message will not appear -->
      <v-alert
        v-if="invalidOrderDataMessage.length > 0 && cart.length <= 0"
        text
        color="error"
      >
        {{ invalidOrderDataMessage }}
      </v-alert>

      <div v-if="cart.length > 0" class="mt-5">
        <reel-order-cart-table
          :cart-items="cart"
          :total="cartTotals.total"
          :subtotal="cartTotals.subtotal"
          :iva="cartTotals.iva"
          :hide-headers="hideHeaders"
          @onDeleteItem="
            (item) => modifyCart({ item: item.reel, value: false }, true)
          "
        >
          <template #footer>
            <cart-weight-modal :cart="cart" />
          </template>
        </reel-order-cart-table>

        <!-- Alerts -->
        <div class="mt-10">
          <v-alert v-if="cartMinimumTotalMessage.length > 0" text color="error">
            {{ cartMinimumTotalMessage }}
          </v-alert>
          <v-alert
            v-if="creditLimitExceededMessage.length > 0"
            text
            color="primary"
          >
            {{ creditLimitExceededMessage }}
          </v-alert>
          <v-alert
            v-if="orderWillBeBlockedMessage.length > 0"
            text
            color="error"
          >
            {{ orderWillBeBlockedMessage }}
          </v-alert>
          <v-alert v-if="invalidOrderDataMessage.length > 0" text color="error">
            {{ invalidOrderDataMessage }}
          </v-alert>
        </div>

        <v-row
          v-if="!$hasAnyRole([Roles.POST_SALE])"
          class="mt-10"
          justify="end"
        >
          <div class="button-container">
            <v-btn class="card-shadow" dark color="#007D36" @click="storeOrder">
              Confirmar pedido
              <v-icon color="white">
                mdi-checkbox-multiple-marked-circle
              </v-icon>
            </v-btn>
          </div>
        </v-row>
      </div>
    </div>

    <!-- Created order success message -->
    <div v-else>
      <v-card class="pa-5 my-10 card-shadow">
        <icon-title
          v-if="orderWillBeBlockedMessage.length > 0"
          :text="orderWillBeBlockedMessage"
          class="mb-5 text-left"
        />
        <icon-title :text="orderCreationResponse.message" />

        <div class="text-left">
          Número de pedido: {{ orderCreationResponse.orderNumber }}
        </div>
        <div
          v-if="orderCreationResponse.shippingNumber.length > 0"
          class="text-left mt-2"
        >
          Número de entrega: {{ orderCreationResponse.shippingNumber }}
        </div>
        <div
          v-if="orderCreationResponse.billNumber.length > 0"
          class="text-left mt-2"
        >
          Número de factura: {{ orderCreationResponse.billNumber }}
        </div>
        <v-row class="mt-15">
          <v-col cols="6">
            <v-btn block color="success" to="/"> Ir a inicio </v-btn>
          </v-col>
          <v-col cols="6">
            <v-btn block color="primary" to="/history-orders">
              Ir a historial
            </v-btn>
          </v-col>
        </v-row>
      </v-card>
    </div>

    <overlay
      :show="loadingMaterial"
      text="Validando información del carrete..."
    />
    <overlay :show="savingOrder" text="Generando pedido..." />

    <!-- Multiple carts alert -->
    <cart-conflict-alert
      v-if="!showCustomerChangedAlert"
      v-model="showSingleCartAlert"
      type="multiple-carts"
    />

    <!-- Customer changed alert -->
    <cart-conflict-alert
      v-model="showCustomerChangedAlert"
      type="customer-changed"
    />
  </v-container>
</template>

<script>
  import TaxDataForm from '@/components/forms/TaxDataForm.vue';
  import OrderInformationForm from '@/components/forms/OrderInfoForm.vue';
  import ReelsTable from '@/components/tables/ReelsTable';
  import ReelOrderCartTable from '@/components/tables/CartOrderTable';
  import SearchInput from '@/components/SearchInput.vue';
  import CustomerOrderInformation from '@/components/CustomerOrderInformation.vue';
  import IconTitle from '@/components/IconTitle.vue';
  import Overlay from '@/components/Overlay.vue';
  import BackBtn from '@/components/BackBtn.vue';
  import BlockedByBackordersAlert from '@/components/BlockedByBackordersAlert';
  import CartWeightModal from './components/CartWeightModal.vue';

  import numbersFormats from '@/mixins/numbersFormats';
  import userBlockedByBackorders from '@/mixins/userBlockedByBackorders';
  import cartTabConsistency from '@/mixins/cartTabConsistency';

  import api from '@/plugins/axios';
  import { mapGetters, mapActions } from 'vuex';
  import { generatePurchaseOrderId } from '@/repositories/Orders';
  import { Roles } from '@/core/constants/roleConstants';
  import maintenance from '@/mixins/maintenance';

  const finantialErrorsCountInit = { commercial: 0, manager: 0, gpom4: 0 };

  export default {
    name: 'CreateReelOrder',

    components: {
      IconTitle,
      TaxDataForm,
      OrderInformationForm,
      ReelsTable,
      ReelOrderCartTable,
      SearchInput,
      CustomerOrderInformation,
      Overlay,
      BackBtn,
      BlockedByBackordersAlert,
      CartWeightModal,
    },
    mixins: [
      numbersFormats,
      userBlockedByBackorders,
      maintenance,
      cartTabConsistency,
    ],
    data() {
      return {
        reelsSelected: [],
        reels: [],

        loadingReels: false,
        loadingMaterial: false,

        cart: [],
        fullReelsTable: false,
        searchReel: '',

        customerData: null,
        customerCreditData: null,
        loadingCustomerData: false,
        customerDataError: false,
        reelErrorMessage: '',

        paymentValues: {
          cfdiRelac: [],
          paymentMethod: [],
          wayToPay: [],
          cfdiUse: [],
        },

        orderForm: {
          orderConcept: '',
          shippingAddressId: '',
          paymentMethod: '',
          wayToPay: '',
          cfdiUse: '',
          documentsTwo: '',
          reBill: '',
          reBillTwo: '',
          materialsText: '',
        },
        shippingAddressList: [],
        invalidOrderDataMessage: '',

        finantialErrorsCount: { ...finantialErrorsCountInit },

        orderWillBeBlockedMessage: '',
        blockStatus: 1,
        orderCreated: false,
        savingOrder: false,
        orderCreationResponse: null,
        Roles,
      };
    },
    computed: {
      cartSubtotal() {
        return this.cart.reduce(
          (sum, currentValue) => sum + parseFloat(currentValue.productPrice),
          0
        );
      },

      cartTotalIva() {
        return this.cart.reduce(
          (sum, currentValue) => sum + parseFloat(currentValue.taxesAmount),
          0
        );
      },

      cartTotal() {
        return this.cartSubtotal + this.cartTotalIva;
      },

      cartTotals() {
        return {
          total: this.formatNumber(this.cartTotal),
          subtotal: this.formatNumber(this.cartSubtotal),
          iva: this.formatNumber(this.cartTotalIva),
        };
      },

      cartProfitMargin() {
        return this.cart.reduce(
          (sum, currentValue) => sum + parseFloat(currentValue.profitMargin),
          0
        );
      },

      cartHasMinimumTotal() {
        return this.cartTotal >= 0;
      },

      customerCredit() {
        const credit = this.customerData?.CREDD || '0';
        const creditNoCommas = credit.replace(/,/gi, '');

        return parseFloat(creditNoCommas);
      },

      isACreditCustomer() {
        const zterm = this.customerData?.ZTERM || '';
        return zterm == 'IU00';
      },

      availableCredit() {
        return Number(this.customerCredit) - Number(this.cartTotal);
      },

      availableCreditFormated() {
        return this.formatNumber(this.availableCredit);
      },

      walletPastDueBalance() {
        const walletPastDueBalance = this.customerCreditData?.wallet?.T7;
        return walletPastDueBalance !== undefined ? walletPastDueBalance : null;
      },

      pastDueBalanceCount() {
        return this.customerCreditData?.pastDueBalancesCount || 0;
      },

      walletPastDueBalanceFormated() {
        if (this.walletPastDueBalance === null) return '';

        return this.formatNumber(this.walletPastDueBalance);
      },

      noCreditProblems() {
        if (
          this.pastDueBalanceCount > 0 ||
          this.customerCredit < 0 ||
          this.availableCredit < 0 ||
          this.customerData?.DOCVENC > 0
        ) {
          return false;
        }

        return true;
      },

      orderStatus() {
        if (this.walletPastDueBalance === null) return 'Análisis Crediticio 2';
        else if (this.availableCredit < 0 || this.walletPastDueBalance < 0)
          return 'Análisis Crediticio';
        else return 'Correcto';
      },

      // Alert Messages
      cartMinimumTotalMessage() {
        if (this.cartTotal > 0 && !this.cartHasMinimumTotal)
          return 'El monto mínimo de compra es $ 5,000.00 MXP'; // MXM ???

        return '';
      },

      creditLimitExceededMessage() {
        if (this.walletPastDueBalance > 0 || this.orderStatus != 'Correcto') {
          return 'Se ha excedido el límite de crédito y/o partidas vencidas. Se generará su pedido.';
        }

        return '';
      },

      fullHeaderReelsTableTextBtn() {
        return !this.fullReelsTable ? 'Tabla completa' : 'Reducir tabla';
      },

      userData() {
        return this.customer;
      },

      hideHeaders() {
        if (this.loggedinUserIsCustomer)
          return ['stockCdpt', 'stock', 'textBox'];

        return ['textBox'];
      },

      ...mapGetters('auth', ['channel', 'userCode', 'user']),
      ...mapGetters('selectedUser', [
        'customer',
        'loadCustomerError',
        'noCustomerSelected',
        'loggedinUserIsCustomer',
      ]),
    },
    async created() {
      this.loadingCustomerData = true;

      this.orderForm.orderConcept = await generatePurchaseOrderId();

      await this.loadCustomer(); // Loads user data if the user is a seller it will load data of a preselected customer

      if (this.noCustomerSelected)
        // If seller is signed and no preselected customer, we redirect to home
        this.$router.replace('/');
      else if (!this.loadCustomerError) {
        // If a error happens while loading preselected customer
        this.validateUserBlockedByBackorders();
        //this.fetchCustomerReminders();
        await this.fetchCustomerInfo();
        this.fetchReels();
      }

      this.sendToPDF({ active: true });
      this.loadingCustomerData = false;
    },
    methods: {
      ...mapActions('selectedUser', ['loadCustomer']),
      ...mapActions('printer', ['sendToPDF']),

      async fetchCustomerInfo() {
        try {
          const customerInfo = api.post('/get-customer-info', this.userData);
          const crediData = api.post(
            '/get-customer-credit-data',
            this.userData
          );
          const addresses = api.post('/destinyCustomer', this.userData);

          const [
            { data },
            { data: creditData },
            { data: customerDestinyList },
          ] = await Promise.all([customerInfo, crediData, addresses]);

          this.customerData = data.customerData;
          const blocked = await this.redirectIfBillingModuleDisabled(
            data.customerData?.OPCFAC
          );
          if (blocked) return;
          this.paymentValues.cfdiRelac = data.cfdiRelac;
          this.paymentValues.paymentMethod = data.paymentMethods;
          this.paymentValues.wayToPay = data.waysToPay;
          this.paymentValues.cfdiUse = data.cfdiUses;
          this.customerCreditData = creditData;
          this.shippingAddressList = customerDestinyList;
        } catch (error) {
          this.customerDataError = true;
        }
      },

      async fetchReels() {
        this.loadingReels = true;

        try {
          const response = await api.get('/reel-list', {
            params: {
              office: this.customerData?.VKBUR,
            },
          });
          this.reels = this.formatReelsData(response.data);
          this.buildSequenceState();
        } catch (error) {
          this.reelErrorMessage = 'No se pudo obtener la lista de carretes';
        }

        this.loadingReels = false;
      },

      async modifyCart({ item, value }, fromSelectedList = true) {
        // FromSelectedList removes the reel table selection when the reel is removed from the cart
        const reelNumber = item.NCARR;

        try {
          if (value) {
            this.loadingMaterial = true;
            const material = await this.fetchMaterial(
              reelNumber,
              item.MATNR,
              item.KWMENG,
              this.userData
            );
            this.cart.push({ ...material, reelNumber, reel: item });
            this.reelsSelected.push(item);
            this.changeReelStatus(reelNumber, 'selected');
          } else {
            this.removeReelFromCart(reelNumber, fromSelectedList);
          }
        } catch (error) {
          this.reelErrorMessage = this.resolveHttpError(
            error,
            [400, 404],
            'Sucedió un error al recuperar la información del carrete'
          );
          if (error?.response?.status === 404) {
            this.removeReels(error.response.data.invalidReels);
          } else {
            this.changeReelStatus(reelNumber, 'error', false);
          }
        }

        this.loadingMaterial = false;
        this.checkOrderBlocks();
        this.enforceSequence();
        this.buildSequenceState();
      },

      async fetchMaterial(reelNumber, materialId, requiredQty, userCreds) {
        this.reelErrorMessage = '';
        const response = await api.post('/reel-material', {
          ...userCreds,
          reelNumber,
          materialId,
          requiredQty,
        });
        return response.data;
      },

      async storeOrder() {
        this.invalidOrderDataMessage = '';
        const dataValidMessage = this.validateOrderData();
        const orderConceptGpom4 =
          this.finantialErrorsCount.gpom4 >= 1 ? '-BC' : '';

        if (dataValidMessage != null) {
          this.invalidOrderDataMessage = dataValidMessage;
        } else {
          const form = {
            ...this.userData,
            ...this.orderForm,
            orderConcept: `${this.orderForm.orderConcept}${orderConceptGpom4}`,
            cart: this.cart,
            blockStatus: this.blockStatus,
            newReelReminderList: [],
            //removeReelReminderList: this.remindersSelected,
            createdBySeller: !this.loggedinUserIsCustomer,
            sellerCode: this.userCode,
          };

          try {
            this.savingOrder = true;
            const storeReelResponse = await api.post('/store-reel-order', form);
            this.orderCreationResponse = storeReelResponse.data;
            this.orderCreated = true;
            this.goToHistory();
          } catch (error) {
            this.invalidOrderDataMessage = this.resolveHttpError(
              error,
              [400, 404],
              'No se pudo crear el pedido'
            );
            if (error?.response?.status === 404) {
              this.removeReels(error.response.data.invalidReels);
            }
          }
          this.savingOrder = false;
        }
      },

      validateOrderData() {
        const invalidOrderCharacter = /[|]|[~]|[¬]|[']/.test(
          this.orderForm.orderConcept
        );

        if (invalidOrderCharacter) {
          return 'Carácter inválido en orden de compra';
        } else if (
          this.orderForm.orderConcept == '' &&
          this.orderForm.shippingAddressId == ''
        ) {
          return 'Favor de completar orden de compra y destino';
        } else if (this.orderForm.orderConcept == '') {
          return 'Favor de completar orden de compra';
        } else if (this.orderForm.shippingAddressId == '') {
          return 'Favor de completar destino';
        } else if (
          this.isACreditCustomer &&
          this.orderForm.paymentMethod == ''
        ) {
          return 'Favor de completar metodo de pago';
        } else if (this.isACreditCustomer && this.orderForm.wayToPay == '') {
          return 'Favor de completar via de pago';
        } else if (this.isACreditCustomer && this.orderForm.cfdiUse == '') {
          return 'Favor de completar uso de CFDI';
        }

        return null;
      },

      removeReelFromCart(reelNumber, fromSelectedList = false) {
        const index = this.cart.findIndex(
          (reel) => reel.reelNumber === reelNumber
        );
        index >= 0 && this.cart.splice(index, 1);

        if (fromSelectedList) {
          // Removes selection when reels is removed from cart table
          const indexSelected = this.reelsSelected.findIndex(
            (reel) => reel.NCARR === reelNumber
          );
          indexSelected >= 0 && this.reelsSelected.splice(indexSelected, 1);
        }
        this.changeReelStatus(reelNumber, 'unSelected');
      },

      checkOrderBlocks() {
        this.blockStatus = 1;

        if (this.customerData?.DOCVENC > 0 || this.customerData?.MONVENC > 0) {
          this.orderWillBeBlockedMessage =
            'El pedido será bloqueado por crédito';
          this.blockStatus = 2;
        } else if (this.noCreditProblems === false) {
          this.orderWillBeBlockedMessage =
            'El pedido será bloqueado para autorización comercial y crédito.';
          this.blockStatus = 2;
        }
      },

      formatReelsData(reels) {
        // isRoot is true when a material appears after a different material (start of a new consecutive block)
        return reels.map((reel, index) => {
          const prev = reels[index - 1];
          const isRoot = index === 0 || prev.MATNR !== reel.MATNR;

          return {
            ...reel,
            isRoot,
            isSelectable: reel.PEDVTA === '' || reel.PEDVTA === null,
            matnr: this.removeLeftZeros(reel.MATNR),
            kwmeng: this.formatNumber(reel.KWMENG, ''),
            POSNR: this.removeLeftZeros(reel.POSNR),
          };
        });
      },

      // Iterates through reels to enforce sequential selection rules.
      // If a break in the selection chain is detected, all subsequent invalid selections are removed.
      enforceSequence() {
        let broken = false;

        const newReels = this.reels.map((reel) => {
          let status = reel.status;

          // A root reel resets the sequence chain
          if (reel.isRoot) {
            broken = false;
          }

          // If the chain is already broken and this reel is selected,
          // it gets automatically deselected and removed from the cart
          if (status === 'selected' && broken) {
            status = 'unSelected';
            this.removeReelFromCart(reel.NCARR, true);
          }

          // Once a non-selected reel is found, the chain is considered broken
          if (status !== 'selected') {
            broken = true;
          }

          return {
            ...reel,
            status,
          };
        });

        this.reels = newReels;
      },

      // Iterates through reels to determine which ones are selectable in the current sequential flow.
      // Selection is only allowed while the chain remains valid.
      buildSequenceState() {
        let broken = false;
        const newReels = this.reels.map((reel) => {
          // Root resets the sequence
          if (reel.isRoot) {
            broken = false;
          }

          // Once broken, everything is not selectable
          const isSequentialSelectable = !broken;

          // If we reach a non-selected reel, the sequence breaks from this point forward
          if (reel.status !== 'selected') {
            broken = true;
          }

          return {
            ...reel,
            isSequentialSelectable,
          };
        });

        this.reels = newReels;
      },

      removeReels(reels) {
        reels.forEach((reel) => {
          this.removeReelFromCart(reel);
          const reelIndex = this.findReelIndex(reel);
          if (reelIndex >= 0) {
            this.reels.splice(reelIndex, 1);
          }
        });

        this.enforceSequence();
        this.buildSequenceState();
      },

      changeReelStatus(reelNumber, status, isSelectable = true) {
        const reelIndex = this.reels.findIndex(
          (reel) => reel.NCARR === reelNumber
        );

        if (reelIndex >= 0) {
          const reel = { ...this.reels[reelIndex] };
          reel.status = status;
          reel.isSelectable = isSelectable;
          this.reels.splice(reelIndex, 1, reel);
        }
      },

      findReelIndex(reelNumber) {
        const reelIndex = this.reels.findIndex(
          (reel) => reel.NCARR === reelNumber
        );

        return reelIndex;
      },

      resolveHttpError(error, statusCodes = [], defaultMessage = '') {
        const { response } = error;

        if (response && statusCodes.includes(response.status))
          return response.data.message;

        return defaultMessage;
      },

      goToHistory() {
        setTimeout(() => {
          this.$router.push('/history-orders');
        }, 5000);
      },
    },
  };
</script>

<style scoped>
  .button-container {
    display: flex;
    justify-content: right;
  }

  .button-container > button {
    width: 400px;
    max-width: 400px;
    border-radius: 8px;
  }

  @media screen and (max-width: 960px) {
    .button-container > button {
      width: 100%;
      max-width: auto;
    }
  }
</style>
